import * as R from 'ramda';
import {
  ArticleStatus,
  GetArticleQuery_query_article_Article,
  GetArticleQuery_query_article_Article_authors_ArticleAuthor,
  GetArticleQuery_query_article_Article_categories_ArticleCategory,
  GetArticleQuery_query_article_Article_channels_ArticleChannel,
  GetArticleQuery_query_article_Article_events_ArticleEvent,
  GetArticleQuery_query_article_Article_guides_ArticleGuide,
  GetArticleQuery_query_article_Article_bibliographies_ArticleBibliography,
  UpsertArticleMutation_mutation_upsertArticle_Article_qualifiers_ArticleQualifier,
  GetArticleQuery_query_article_Article_tags_ArticleTag,
  UpsertArticleMutationVariables,
  TagType,
} from '../../../__generated__/queries-web';
import { ArticleState } from '../reducer';
import { RawDraftContentState } from 'draft-js';
import { OneMedia } from '@prismamedia/one-components';

interface BasicArticleInfo {
  title: string;
  secondaryTitle: string | null | undefined;
  partnerTitle: string | null | undefined;
  discoverTitle: string | null | undefined;
  medias: string | null | OneMedia[];
  lead: string | null | RawDraftContentState;
  body: string | null | RawDraftContentState;
  publishedAt: string | null;
  unpublishAt: string | null;
  slug?: string | null;
  status: ArticleStatus;
}
interface queryType {
  create?: unknown[];
  delete?: unknown[];
}

const parseBasicInfo = (obj: BasicArticleInfo) =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      typeof value === 'string' && !value ? null : value,
    ]),
  ) as BasicArticleInfo;

const getBasicArticleInfo = (article: ArticleState): BasicArticleInfo =>
  parseBasicInfo({
    title: article.title,
    secondaryTitle: article.secondaryTitle,
    discoverTitle: article.discoverTitle,
    partnerTitle: article.partnerTitle,
    medias: article.medias?.length ? article.medias : null,
    lead: article.lead ? article.lead : null,
    body: article.body ? article.body : null,
    publishedAt: article.publishedAt
      ? new Date(article.publishedAt).toISOString()
      : null,
    unpublishAt: article.unpublishAt
      ? new Date(article.unpublishAt).toISOString()
      : null,
    ...(article.slug && { slug: article.slug }),
    status: article.status,
  });

export const getCreateForResource = <R extends { [key in string]: any }>(
  resources: R[],
  resourceSubKey: keyof R,
): any => {
  return resources?.map((resource: R) => ({
    [resourceSubKey]: {
      connect: { id: resource[resourceSubKey].id },
    },
  }));
};

const getCreateProgramCollectionTVTags = (
  programCollectionTags?: GetArticleQuery_query_article_Article_tags_ArticleTag[],
) => {
  if (programCollectionTags) {
    return programCollectionTags.map((tag) => {
      const { title, type, relationId } = tag.tag;
      return {
        tag: {
          createIfNotExists: {
            where: { relationId, type },
            data: {
              title,
              type,
              relationId,
            },
          },
        },
      };
    });
  }
  return [];
};

export const mapDeleteRelationship = R.map(R.pick(['order']));

const purgeByPath = (path: string, obj: UpsertArticleMutationVariables) => {
  const properties = [
    'authors',
    'categories',
    'qualifiers',
    'tags',
    'channels',
    'events',
    'guides',
    'machineTags',
    'histories',
    'bibliographies',
  ];

  let result = { ...obj };
  // eslint-disable-next-line fp/no-loops
  for (const property of properties) {
    if (
      !R.pathSatisfies(
        (query: queryType) =>
          Boolean(query?.create?.length) || Boolean(query?.delete?.length),
        [path, property],
        result,
      )
    ) {
      result = R.dissocPath([path, property], result);
    }
  }
  return result;
};

const purgeResult = (obj: UpsertArticleMutationVariables) => {
  let result = { ...obj };
  result = purgeByPath('create', result);
  result = purgeByPath('update', result);
  return result;
};

export const articleMutationVariables = (
  article: ArticleState,
  prevArticle?: GetArticleQuery_query_article_Article | null,
  editedAt?: string | null,
): UpsertArticleMutationVariables => {
  const createAuthors = getCreateForResource<
    GetArticleQuery_query_article_Article_authors_ArticleAuthor
  >(article.authors, 'author');
  const createCategories = getCreateForResource<
    GetArticleQuery_query_article_Article_categories_ArticleCategory
  >(article.categories, 'category');
  const createQualifiers = getCreateForResource<
    UpsertArticleMutation_mutation_upsertArticle_Article_qualifiers_ArticleQualifier
  >(article.qualifiers, 'qualifier');
  // Create ProgamTv tags
  const programTvTags = article.tags.filter(
    (tag) => tag.tag.type === TagType.TvProgram,
  );

  // Create Collection tags
  const collectionTvTags = article.tags.filter(
    (tag) => tag.tag.type === TagType.TvCollection,
  );

  // Create other tags
  const createOtherTags = getCreateForResource<
    GetArticleQuery_query_article_Article_tags_ArticleTag
  >(
    article.tags.filter(
      (tag) =>
        ![TagType.TvProgram, TagType.TvCollection].includes(tag.tag.type),
    ),
    'tag',
  );

  const createTags = [
    ...getCreateProgramCollectionTVTags(programTvTags),
    ...getCreateProgramCollectionTVTags(collectionTvTags),
    ...createOtherTags,
  ];

  const createChannels = getCreateForResource<
    GetArticleQuery_query_article_Article_channels_ArticleChannel
  >(article.channels, 'category');
  const createEvents = getCreateForResource<
    GetArticleQuery_query_article_Article_events_ArticleEvent
  >(article.events, 'category');
  const createGuides = getCreateForResource<
    GetArticleQuery_query_article_Article_guides_ArticleGuide
  >(article.guides, 'category');

  const createBibliographies = (article.bibliographies || []).map(
    (
      bibliography: GetArticleQuery_query_article_Article_bibliographies_ArticleBibliography,
    ) => ({
      title: bibliography.title,
      url: bibliography.url,
    }),
  );

  const articleStoryDuplicationMachineTag =
    article?.machineTags &&
    article.machineTags.length === 1 &&
    article.machineTags[0].machineTag.tag.startsWith('topicassignment:id');

  const result = purgeResult({
    create: {
      id: article.id,
      ...getBasicArticleInfo(article),
      ...(editedAt || article.status === ArticleStatus.Published
        ? {
            editedAt: editedAt
              ? new Date(editedAt).toISOString()
              : new Date().toISOString(),
          }
        : {}),
      brandKey: article.brandKey,
      format: article.format,
      authors: {
        create: createAuthors,
      },
      categories: {
        create: createCategories,
      },
      qualifiers: {
        create: createQualifiers,
      },
      tags: {
        create: createTags,
      },
      channels: {
        create: createChannels,
      },
      events: {
        create: createEvents,
      },
      guides: {
        create: createGuides,
      },
      bibliographies: {
        create: createBibliographies,
      },
      // The only case where a machine tag is created is when the article is duplicated
      // from Story
      ...(articleStoryDuplicationMachineTag
        ? {
            machineTags: {
              create: [
                {
                  machineTag: {
                    createIfNotExists: {
                      where: {
                        id: article.machineTags?.[0]?.machineTag.id,
                      },
                      data: {
                        id: article.machineTags?.[0]?.machineTag.id,
                        tag: article.machineTags?.[0]?.machineTag.tag,
                      },
                    },
                  },
                },
              ],
            },
          }
        : {}),
      source: article.source ? { connect: { id: article.source.id } } : null,
    },
    ...(prevArticle
      ? {
          update: {
            ...getBasicArticleInfo(article),
            ...(editedAt || article.status === ArticleStatus.Published
              ? {
                  editedAt: editedAt
                    ? new Date(editedAt).toISOString()
                    : new Date().toISOString(),
                }
              : {}),
            authors: {
              create: createAuthors,
              delete: mapDeleteRelationship(prevArticle.authors),
            },
            categories: {
              create: createCategories,
              delete: mapDeleteRelationship(prevArticle.categories),
            },
            qualifiers: {
              create: createQualifiers,
              delete: mapDeleteRelationship(prevArticle.qualifiers),
            },
            tags: {
              create: createTags,
              delete: mapDeleteRelationship(prevArticle.tags),
            },
            channels: {
              create: createChannels,
              delete: mapDeleteRelationship(prevArticle.channels),
            },
            events: {
              create: createEvents,
              delete: mapDeleteRelationship(prevArticle.events),
            },
            guides: {
              create: createGuides,
              delete: mapDeleteRelationship(prevArticle.guides),
            },
            bibliographies: {
              create: createBibliographies,
              deleteAll: true,
            },
            source: article.source
              ? { connect: { id: article.source.id } }
              : prevArticle.source
              ? { disconnect: true }
              : null,
          },
        }
      : {
          update: {},
        }),
    where: {
      id: article.id,
    },
  });
  return result;
};
